import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Crown, Check, Medal } from "@phosphor-icons/react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "./ui/dialog";

export async function startCheckout(API, lookupKey = "pro_monthly") {
  try {
    const { data } = await axios.post(`${API}/payments/checkout`,
      { origin_url: window.location.origin, lookup_key: lookupKey },
      { withCredentials: true });
    window.location.href = data.checkout_url;
  } catch {
    toast.error("Couldn't start checkout. Please try again.");
  }
}

export const TIERS = [
  {
    name: "Amateur", color: "#2563EB", Icon: Medal,
    monthly: { key: "amateur_monthly", price: "$4.99", per: "/month" },
    yearly: { key: "amateur_yearly", price: "$49.99", per: "/year", save: "2 months free" },
    perks: ["5 parlay builds per day", "All 4 sports — NFL, NBA, MLB, NHL", "EV + confidence numbers", "Risk curve on every ticket"],
  },
  {
    name: "Pro", color: "#FFD700", Icon: Crown, badge: "Best value",
    monthly: { key: "pro_monthly", price: "$9.99", per: "/month" },
    yearly: { key: "pro_yearly", price: "$99.99", per: "/year", save: "2 months free" },
    perks: ["Everything in Amateur", "Unlimited parlay builds", "Daily Best Value picks", "Morning streak tracking"],
  },
];

export const BillingToggle = ({ cycle, setCycle }) => (
  <div className="inline-flex rounded-full bg-black/40 border border-white/10 p-1" data-testid="billing-toggle">
    {["monthly", "yearly"].map((c) => (
      <button key={c} onClick={() => setCycle(c)} data-testid={`billing-${c}`}
        className={`rounded-full px-4 py-1.5 text-xs font-bold uppercase tracking-wide transition-colors ${
          cycle === c ? "bg-[#00E676] text-black" : "text-slate-400 hover:text-white"}`}>
        {c === "monthly" ? "Monthly" : "Yearly · save 17%"}
      </button>
    ))}
  </div>
);

export const PaywallDialog = ({ open, onOpenChange, message, API }) => {
  const [cycle, setCycle] = useState("monthly");
  return (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="bg-[#141419] border-white/10 text-slate-50 max-w-2xl" data-testid="paywall-dialog">
      <div className="pt-2">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-1">
          <DialogTitle className="font-display text-3xl font-black uppercase tracking-tight">Pick your plan</DialogTitle>
          <BillingToggle cycle={cycle} setCycle={setCycle} />
        </div>
        {message
          ? <DialogDescription className="text-sm text-[#FFD700] mb-5" data-testid="paywall-message">{message}</DialogDescription>
          : <DialogDescription className="text-sm text-slate-400 mb-5">Cancel anytime from the billing portal.</DialogDescription>}
        <div className="grid sm:grid-cols-2 gap-4">
          {TIERS.map(({ name, color, Icon, badge, perks, ...tier }) => {
            const p = tier[cycle];
            return (
            <div key={name} className="rounded-xl border p-5 flex flex-col"
              style={{ borderColor: `${color}55`, background: `${color}0d` }}>
              <div className="flex items-center justify-between mb-1">
                <p className="font-display text-2xl font-black uppercase tracking-tight flex items-center gap-1.5" style={{ color }}>
                  <Icon size={20} weight="fill" /> {name}
                </p>
                {badge && <span className="text-[10px] font-black uppercase rounded-full px-2 py-0.5" style={{ background: color, color: "#000" }}>{badge}</span>}
              </div>
              <p className="text-sm text-slate-400 mb-1"><span className="text-white font-bold text-lg">{p.price}</span>{p.per}</p>
              {p.save ? <p className="text-[11px] font-bold text-[#00E676] mb-3">{p.save}</p> : <p className="mb-3" />}
              <ul className="space-y-2 mb-5 flex-1">
                {perks.map((perk) => (
                  <li key={perk} className="flex items-start gap-2 text-xs text-slate-300">
                    <Check size={14} weight="bold" className="shrink-0 mt-0.5" style={{ color }} /> {perk}
                  </li>
                ))}
              </ul>
              <button onClick={() => startCheckout(API, p.key)} data-testid={`upgrade-checkout-btn-${name.toLowerCase()}`}
                className="w-full rounded-full font-bold py-2.5 text-sm hover:scale-[1.02] active:scale-95 transition-transform"
                style={{ background: color, color: "#000" }}>
                Get {name}
              </button>
            </div>
            );
          })}
        </div>
        <p className="text-[11px] text-slate-500 mt-4 text-center">
          Test mode · card 4242 4242 4242 4242 · have a promo code? Enter it at checkout
        </p>
      </div>
    </DialogContent>
  </Dialog>
  );
};
